import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Box, Grid, TableCell, TableRow, Typography } from '@mui/material'

import { StateSchema } from '@/hooks/form/models'
import { SportRestrictionObject } from '@/state/bonus-engine/models'
import NumberFormatCustom from '@/views/components/input-number-format/index.component'

import CurrenciesTableComponent from '@/views/components/currencies-table/currencies-table.components'
import { authSelector } from '@/state/auth/slices/index.slices'
import { BonusType, CurrencyCodeEnum } from '@/enums'

import SportTabsRestrictions from './sport-tabs.component'
import ExpirationComponent from '../expiration-time.component'
import RolloverCalculationComponent from './rollover-calculation.component'

type Props = {
  handleOnChange: (name: string, value: any) => void
  state: StateSchema
  handleOnClickSetEnable: (
    type: string,
    value: number | string,
    all?: boolean,
  ) => void
  handleOnClickSetDisable: (
    type: string,
    value: number | string,
    all?: boolean,
  ) => void
  handleOnClickSetDiscardContribution: (
    type: string,
    value: number | string,
  ) => void
  getProductsInitValues: () => SportRestrictionObject[]
  getWagerTypeInitValues: (hideElement?: boolean) => SportRestrictionObject[]
  getSportInitValues: (hideElement?: boolean) => SportRestrictionObject[]
  bonusType: string
  rolloverRestrictions: boolean
  hideTypes?: boolean
  title: string
}

const SportRestrictions = ({
  handleOnChange,
  state,
  handleOnClickSetDisable,
  handleOnClickSetDiscardContribution,
  handleOnClickSetEnable,
  getProductsInitValues,
  getSportInitValues,
  getWagerTypeInitValues,
  bonusType,
  rolloverRestrictions,
  hideTypes,
  title,
}: Props) => {
  const { t } = useTranslation()

  const currencyHeadCells = ['baseCurrency', 'bonusMaxBet', 'bonusMaxWin']
  // bonusType !== BonusType.FREE_PLAY
  //   ? ['baseCurrency', 'bonusMaxBet', 'bonusMaxWin']
  //   : ['baseCurrency', 'bonusMaxBet']

  const minResetRealMoneyAmountCells = ['baseCurrency', 'labels.amount']

  const { brand } = useSelector(authSelector)

  return (
    <>
      <Typography sx={{ marginBottom: '4px' }}>{t(title)}</Typography>
      <Box mt={2} maxWidth={800}>
        <Typography fontWeight={700} sx={{ marginTop: '15px' }}>
          {t('restrictions.definitionRules')}
        </Typography>
        <Box mt={2}>
          <SportTabsRestrictions
            handleOnChange={handleOnChange}
            state={state}
            handleOnClickSetDisable={handleOnClickSetDisable}
            handleOnClickSetEnable={handleOnClickSetEnable}
            handleOnClickSetDiscardContribution={
              handleOnClickSetDiscardContribution
            }
            getProductsInitValues={getProductsInitValues}
            getSportInitValues={getSportInitValues}
            getWagerTypeInitValues={getWagerTypeInitValues}
            bonusType={bonusType}
            hideTypes={hideTypes}
          />
        </Box>
      </Box>
      <Box mt={5} maxWidth={800}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box mb={2} maxWidth={400}>
              <NumberFormatCustom
                name="minLine"
                label={t('restrictions.minLine')}
                handleOnChange={handleOnChange}
                state={state}
                prefix="+"
                disableInputPropsAdornment
                fullwidth
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box mb={2} maxWidth={400}>
              <NumberFormatCustom
                name="maxLine"
                label={t('restrictions.maxLine')}
                handleOnChange={handleOnChange}
                onValueChange={({ value: v }) => {
                  if (Number(v) > 0) {
                    handleOnChange('maxLine', -v)
                  } else {
                    handleOnChange('maxLine', v)
                  }
                }}
                state={state}
                prefix=""
                disableInputPropsAdornment
                fullwidth
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
      {!rolloverRestrictions ? (
        <>
          <Box my={5} maxWidth={800}>
            <Grid container>
              <Grid item xs={12}>
                <Typography fontWeight={700} gutterBottom sx={{ mb: '20px' }}>
                  {t('maxBetAndWin')}
                  {/* {bonusType !== BonusType.FREE_PLAY
                    ? t('maxBetAndWin')
                    : t('bonusMaxBet')} */}
                </Typography>
                <CurrenciesTableComponent currencyHeadCells={currencyHeadCells}>
                  {brand.currencies.map((currency) => (
                    <TableRow key={`sports_${currency}`}>
                      <TableCell>
                        {currency === CurrencyCodeEnum.USD
                          ? `${currency} ${t('labels.currencyDefault')}`
                          : currency}
                      </TableCell>
                      <TableCell
                        sx={{
                          width:
                            // bonusType !== BonusType.FREE_PLAY ?
                            300,
                          // : 400 || 'auto',
                        }}
                      >
                        <NumberFormatCustom
                          name={`maxBet_${currency}`}
                          handleOnChange={handleOnChange}
                          state={state}
                          currency={currency}
                          prefix=""
                          style={{ padding: '16px 0' }}
                          fullwidth
                        />
                      </TableCell>
                      {/* {bonusType !== BonusType.FREE_PLAY && ( */}
                      <TableCell sx={{ width: 300 || 'auto' }}>
                        <NumberFormatCustom
                          name={`maxWin_${currency}`}
                          handleOnChange={handleOnChange}
                          state={state}
                          currency={currency}
                          prefix=""
                          style={{ padding: '16px 0' }}
                          fullwidth
                        />
                      </TableCell>
                      {/* )} */}
                    </TableRow>
                  ))}
                </CurrenciesTableComponent>
              </Grid>
            </Grid>
          </Box>
          <ExpirationComponent handleOnChange={handleOnChange} state={state} />
          <Box mt={3} maxWidth={400}>
            <Typography fontWeight={700} sx={{ mb: '18px' }}>
              {t('maxBonusesAllowedDescription')}
            </Typography>
            <NumberFormatCustom
              name="maxBonusesAllowed"
              label={t('labels.maxBonusesAllowed')}
              handleOnChange={handleOnChange}
              state={state}
              prefix=""
              fullwidth
              InputPropsAdornment={{
                endAdornment: (
                  <Box pr={1}>
                    <Typography variant="body2" component="p">
                      {t('labels.lifetime')}
                    </Typography>
                  </Box>
                ),
              }}
            />
          </Box>
        </>
      ) : (
        <>
          <RolloverCalculationComponent
            handleOnChange={handleOnChange}
            state={state}
          />
          {process.env.REACT_APP_STAGE === 'stg' && (
            <Box my={2} maxWidth={800}>
              <Typography fontWeight={700} gutterBottom sx={{ mb: '20px' }}>
                {t('minResetRealMoneyAmount')}
              </Typography>
              <CurrenciesTableComponent
                currencyHeadCells={minResetRealMoneyAmountCells}
              >
                {brand.currencies.map((currency) => (
                  <TableRow key={`minResetRealMoneyAmount_${currency}`}>
                    <TableCell>
                      {currency === CurrencyCodeEnum.USD
                        ? `${currency} ${t('labels.currencyDefault')}`
                        : currency}
                    </TableCell>
                    <TableCell sx={{ width: 300 || 'auto' }}>
                      <NumberFormatCustom
                        name={`minResetRealMoneyAmount_${currency}`}
                        handleOnChange={handleOnChange}
                        state={state}
                        currency={currency}
                        style={{ padding: '16px 0' }}
                        prefix=""
                        fullwidth
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </CurrenciesTableComponent>
            </Box>
          )}
        </>
      )}
    </>
  )
}

export default SportRestrictions
